import { useMemo } from "react";
import { getGoogleMapLink } from "@b2bportal/core-utilities";
import type { HomesReservation } from "@b2bportal/homes-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import { formatDateWithWeekday } from "@/utilities/formatDate";

export const useHomesReservationDetails = (reservation: HomesReservation) => {
  const { t, formatDateTime, formatFiatCurrency } = useI18nContext();

  return useMemo(() => {
    const listing = reservation.listing;
    const stayDates = reservation.stayDates;
    const guests = reservation.guestsSelection;

    const checkInDate = formatDateWithWeekday(
      dayjs(stayDates.from).toDate(),
      formatDateTime
    );
    const checkOutDate = formatDateWithWeekday(
      dayjs(stayDates.until).toDate(),
      formatDateTime
    );

    const guestParts: string[] = [];
    if (guests.adults > 0) {
      guestParts.push(
        `${guests.adults} ${guests.adults === 1 ? t("guest", { defaultValue: "guest" }) : t("guests", { defaultValue: "guests" })}`
      );
    }
    if (guests.children > 0) {
      guestParts.push(
        `${guests.children} ${guests.children === 1 ? t("child", { defaultValue: "child" }) : t("children", { defaultValue: "children" })}`
      );
    }
    if (guests.infants > 0) {
      guestParts.push(
        `${guests.infants} ${guests.infants === 1 ? t("infant", { defaultValue: "infant" }) : t("infants", { defaultValue: "infants" })}`
      );
    }
    const guestSummary = guestParts.join(", ");

    const primaryGuestName = [
      reservation.primaryGuest.givenName,
      reservation.primaryGuest.surname,
    ]
      .filter(Boolean)
      .join(" ");

    return {
      image: listing.coverImage,
      name: listing.name,
      location: listing.areaName,
      checkInDate,
      checkOutDate,
      guestSummary,
      primaryGuestName,
      address: listing.address,
      mapsUrl:
        listing.address !== ""
          ? getGoogleMapLink({
              placeName: listing.name,
              address: listing.address,
              coordinates: listing.location?.coordinates,
            })
          : "",
      hostPhone:
        reservation.hostInfo?.contacts?.find((c) => c.type === "Primary")
          ?.phoneNumber ?? "",
      hostEmail:
        reservation.hostInfo?.contacts?.find((c) => c.type === "Primary")
          ?.email ?? "",
      confirmationCode: reservation.id.value,
      lengthOfStay: reservation.lengthOfStay,
      sellPrice: formatFiatCurrency(
        reservation.rate.detailedPrice.sellPrice.fiat
      ),
      lineItems: reservation.rate.detailedPrice.groupedItems.map((item) => ({
        label: item.displayName,
        value: formatFiatCurrency(item.total.fiat),
      })),
      totalTaxesAndFees: reservation.rate.detailedPrice.totalTaxesAndFees
        ? formatFiatCurrency(
            reservation.rate.detailedPrice.totalTaxesAndFees.amount.fiat
          )
        : undefined,
      total: formatFiatCurrency(
        reservation.rate.detailedPrice.payNowTotal.fiat
      ),
    };
  }, [reservation, formatDateTime, formatFiatCurrency, t]);
};
